import * as THREE from 'three';

export type HeadLockedHintOptions = {
  /** Distance ahead of the eyes (m). */
  distance?: number;
  /** Drop below eye line (m). */
  drop?: number;
  /** Plane width (m). */
  width?: number;
};

const CANVAS_W = 1024;
const CANVAS_H = 128;

/**
 * A single line of lazily head-following text for instructions and transient
 * status ("pinch to place the tee", "hands paused — swing away"). Unlike the
 * ground HUD this must stay readable wherever the player looks — but a
 * hard-locked HUD is nauseating in a headset, so it eases toward the view
 * direction instead of sticking to it.
 */
export class HeadLockedHint {
  object: THREE.Mesh;

  #canvas: HTMLCanvasElement;
  #ctx: CanvasRenderingContext2D;
  #texture: THREE.CanvasTexture;
  #text = '';
  /** Seconds left on a self-dismissing line; 0 means hold until replaced. */
  #hold = 0;
  #distance: number;
  #drop: number;
  #snapNext = true;
  #desired = new THREE.Vector3();
  #forward = new THREE.Vector3();

  constructor(options: HeadLockedHintOptions = {}) {
    this.#distance = options.distance ?? 1.4;
    this.#drop = options.drop ?? 0.35;
    const width = options.width ?? 0.95;

    this.#canvas = document.createElement('canvas');
    this.#canvas.width = CANVAS_W;
    this.#canvas.height = CANVAS_H;
    const ctx = this.#canvas.getContext('2d');
    if (!ctx) throw new Error('Unable to get 2d context for hint');
    this.#ctx = ctx;
    this.#texture = new THREE.CanvasTexture(this.#canvas);
    this.#texture.colorSpace = THREE.SRGBColorSpace;

    this.object = new THREE.Mesh(
      new THREE.PlaneGeometry(width, width * (CANVAS_H / CANVAS_W)),
      new THREE.MeshBasicMaterial({
        map: this.#texture,
        transparent: true,
        depthTest: false, // always readable, never clipped by the world
        fog: false,
      })
    );
    this.object.name = 'HeadLockedHint';
    this.object.renderOrder = 30;
    this.object.userData.mrMaskExempt = true;
    this.object.visible = false;
  }

  /**
   * Show a line, replacing whatever is up.
   *
   * `holdSeconds` makes it dismiss itself. Announcements that REPORT something
   * already done — "window framed to your room" — have no business sitting in
   * the player's view for the rest of the session, while instructions that ASK
   * for something ("pull the trigger") have to stay until the player resolves
   * them. Holding forever is therefore still the default.
   */
  setText(text: string, holdSeconds = 0) {
    if (text === this.#text) return;
    this.#text = text;
    this.#hold = holdSeconds;
    if (!text) {
      this.object.visible = false;
      return;
    }
    this.#draw(text);
    if (!this.object.visible) this.#snapNext = true;
    this.object.visible = true;
  }

  update(dt: number, headPosition: THREE.Vector3, headQuaternion: THREE.Quaternion) {
    if (!this.object.visible) return;

    if (this.#hold > 0) {
      this.#hold -= dt;
      if (this.#hold <= 0) {
        this.setText('');
        return;
      }
    }

    this.#forward.set(0, 0, -1).applyQuaternion(headQuaternion);
    this.#desired.copy(headPosition).addScaledVector(this.#forward, this.#distance);
    this.#desired.y -= this.#drop;

    if (this.#snapNext) {
      this.object.position.copy(this.#desired);
      this.#snapNext = false;
    } else {
      // Ease toward the view direction — comfortable, not glued to the face
      this.object.position.lerp(this.#desired, 1 - Math.exp(-4 * dt));
    }
    this.object.lookAt(headPosition);
  }

  #draw(text: string) {
    const ctx = this.#ctx;
    ctx.clearRect(0, 0, CANVAS_W, CANVAS_H);
    ctx.font = 'bold 52px Rubik, sans-serif';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';

    const padding = 48;
    const textWidth = Math.min(ctx.measureText(text).width + padding * 2, CANVAS_W);
    ctx.fillStyle = 'rgba(17, 28, 28, 0.82)';
    ctx.beginPath();
    ctx.roundRect((CANVAS_W - textWidth) / 2, 8, textWidth, CANVAS_H - 16, 56);
    ctx.fill();

    ctx.fillStyle = '#ffffff';
    ctx.fillText(text, CANVAS_W / 2, CANVAS_H / 2 + 2, CANVAS_W - padding * 2);
    this.#texture.needsUpdate = true;
  }
}
